/**
 * Admin Settings → Termini di Pagamento (CRUD).
 * Configura i payment terms usati nelle anagrafiche clienti e per il calcolo
 * della due_date dei documenti di vendita.
 */
import React, { useEffect, useState } from "react";
import axios from "axios";
import { Plus, Edit2, Trash2, Save, X } from "lucide-react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Switch } from "../../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../../components/ui/dialog";
import { toast } from "sonner";
import { API } from "../../utils/format";
import { FieldHint } from "../../components/ui/FieldHint";

const EMPTY = { code: "", name: "", days: 0, is_advance: false, end_of_month: false, sort_order: 100 };

const PaymentTermsManagement = () => {
  const [terms, setTerms] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = async () => {
    try {
      const r = await axios.get(`${API}/payment-terms`);
      setTerms(r.data || []);
    } catch (e) {
      toast.error("Errore nel caricamento dei termini");
    }
  };
  useEffect(() => { load(); }, []);

  const startNew = () => { setEditing("new"); setForm(EMPTY); };
  const startEdit = (t) => { setEditing(t.id); setForm({ ...t }); };
  const cancel = () => { setEditing(null); setForm(EMPTY); };

  const save = async () => {
    if (!form.code || !form.name) { toast.error("Codice e nome obbligatori"); return; }
    try {
      if (editing === "new") {
        await axios.post(`${API}/payment-terms`, form);
        toast.success("Termine creato");
      } else {
        await axios.patch(`${API}/payment-terms/${editing}`, form);
        toast.success("Termine aggiornato");
      }
      cancel();
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const remove = async (id) => {
    if (!window.confirm("Disattivare questo termine? Le anagrafiche esistenti rimangono invariate.")) return;
    try {
      await axios.delete(`${API}/payment-terms/${id}`);
      toast.success("Termine disattivato");
      load();
    } catch (e) {
      toast.error("Errore");
    }
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-stone-900">💳 Termini di Pagamento</h1>
          <p className="text-sm text-stone-600 mt-1">
            Configura i termini di pagamento usati nelle anagrafiche clienti. Ogni termine
            determina la <strong>data di scadenza</strong> dei documenti di vendita.
          </p>
        </div>
        <Button onClick={startNew} className="bg-[#8A1C2A] hover:bg-[#6B1420] text-white" data-testid="add-payment-term-btn">
          <Plus className="w-4 h-4 mr-2" /> Nuovo termine
        </Button>
      </div>

      <Card className="p-0 overflow-hidden bg-white border-stone-200">
        <table className="w-full text-sm">
          <thead className="bg-stone-100 text-left">
            <tr>
              <th className="p-3">Ordine</th>
              <th className="p-3">Codice</th>
              <th className="p-3">Nome</th>
              <th className="p-3 text-right">Giorni</th>
              <th className="p-3">Tipo</th>
              <th className="p-3 text-right">Azioni</th>
            </tr>
          </thead>
          <tbody>
            {terms.length === 0 && (
              <tr><td colSpan={6} className="p-8 text-center text-stone-500 italic">Nessun termine. Verranno auto-creati 8 di default al primo caricamento.</td></tr>
            )}
            {terms.map((t) => (
              <tr key={t.id} className="border-t border-stone-100 hover:bg-stone-50" data-testid={`payment-term-row-${t.code}`}>
                <td className="p-3 text-stone-500">{t.sort_order}</td>
                <td className="p-3 font-mono text-stone-800">{t.code}</td>
                <td className="p-3 text-stone-900">{t.name}</td>
                <td className="p-3 text-right">{t.days}</td>
                <td className="p-3">
                  {t.is_advance && <span className="px-2 py-0.5 bg-orange-100 text-orange-800 rounded text-xs font-semibold">ANTICIPATO</span>}
                  {t.end_of_month && <span className="ml-1 px-2 py-0.5 bg-blue-100 text-blue-800 rounded text-xs">FM</span>}
                  {!t.is_advance && !t.end_of_month && <span className="text-stone-400 text-xs">data fattura</span>}
                </td>
                <td className="p-3 text-right">
                  <Button size="sm" variant="outline" onClick={() => startEdit(t)} className="mr-1" data-testid={`edit-payment-term-${t.code}`}><Edit2 className="w-3 h-3" /></Button>
                  <Button size="sm" variant="outline" onClick={() => remove(t.id)} className="text-red-700 border-red-300" data-testid={`delete-payment-term-${t.code}`}><Trash2 className="w-3 h-3" /></Button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      <Dialog open={!!editing} onOpenChange={(o) => !o && cancel()}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>{editing === "new" ? "Nuovo termine di pagamento" : "Modifica termine"}</DialogTitle>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label className="flex items-center gap-1">Codice
                <FieldHint>Codice corto identificativo (es. <code>30DF</code>, <code>ANT</code>).</FieldHint>
              </Label>
              <Input value={form.code} onChange={(e) => setForm({ ...form, code: e.target.value.toUpperCase() })} placeholder="30DF" data-testid="payment-term-code-input" />
            </div>
            <div>
              <Label>Ordine</Label>
              <Input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} />
            </div>
            <div className="col-span-2">
              <Label>Nome</Label>
              <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} placeholder="30 gg data fattura (DF)" data-testid="payment-term-name-input" />
            </div>
            <div className="col-span-2">
              <Label className="flex items-center gap-1">Giorni
                <FieldHint>Quanti giorni dopo la data del documento (o dalla fine mese se "Fine mese"). Imposta <code>0</code> per anticipato/contanti.</FieldHint>
              </Label>
              <Input type="number" min="0" value={form.days} onChange={(e) => setForm({ ...form, days: parseInt(e.target.value) || 0 })} data-testid="payment-term-days-input" />
            </div>
            <div className="col-span-2 flex items-center justify-between p-3 bg-stone-50 rounded border border-stone-200">
              <div>
                <Label className="flex items-center gap-1">Anticipato
                  <FieldHint>Se attivo, il magazzino vedrà un <strong>warning</strong> e l'ordine non sarà spedito finché non viene registrato l'incasso.</FieldHint>
                </Label>
              </div>
              <Switch checked={form.is_advance} onCheckedChange={(v) => setForm({ ...form, is_advance: v })} data-testid="payment-term-advance-switch" />
            </div>
            <div className="col-span-2 flex items-center justify-between p-3 bg-stone-50 rounded border border-stone-200">
              <div>
                <Label className="flex items-center gap-1">Fine mese (FM)
                  <FieldHint>Se attivo, i giorni vengono contati a partire dall'<strong>ultimo giorno del mese</strong> della data del documento (es. 30FM = fine mese + 30 gg).</FieldHint>
                </Label>
              </div>
              <Switch checked={form.end_of_month} onCheckedChange={(v) => setForm({ ...form, end_of_month: v })} data-testid="payment-term-fm-switch" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={cancel}><X className="w-4 h-4 mr-1" /> Annulla</Button>
            <Button onClick={save} className="bg-[#8A1C2A] text-white" data-testid="save-payment-term-btn"><Save className="w-4 h-4 mr-1" /> Salva</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PaymentTermsManagement;
